import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtPrice, mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { ProductCard } from "@/components/ProductCard";
import { Reviews } from "@/components/Reviews";
import { WishlistButton } from "@/components/WishlistButton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Star, Minus, Plus, ShieldCheck, Truck, Loader2, ChevronLeft, Package, Tag, Store, CheckCircle2, Banknote, Smartphone, Clock, Zap, MapPin } from "lucide-react";
import { toast } from "sonner";
const CAT_LABELS = {
  food: { fr: "Alimentation & Épices", en: "Food & Groceries" },
  fashion: { fr: "Mode & Pagnes", en: "Fashion & Fabrics" },
  electronics: { fr: "Électronique", en: "Electronics" },
  home: { fr: "Maison & Déco", en: "Home & Living" },
  beauty: { fr: "Beauté & Soins", en: "Beauty & Care" },
  crafts: { fr: "Artisanat", en: "Crafts & Art" },
};


export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { t, lang } = useLang();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setProduct(null);
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => navigate("/catalog"));
    api.get(`/products/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [id]); // eslint-disable-line

  if (!product) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-800" /></div>;

  const refreshRating = () => api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => {});
  const soldOut = product.stock <= 0;
  const buyNow = () => { add(product, qty); navigate("/cart"); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-stone-500 hover:text-emerald-800 mb-6" data-testid="back-link"><ChevronLeft className="w-4 h-4" /> {t("continue_shopping")}</button>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="rounded-2xl overflow-hidden bg-stone-100 border border-stone-200 aspect-square img-zoom-wrap shadow-lg">
          <img src={mediaUrl(product.image)} alt={product.name} className="w-full h-full object-cover img-zoom" data-testid="product-image" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-stone-400 mb-2">{t("by")} <Link to={`/shop/${product.seller_id}`} className="text-emerald-800 font-semibold hover:text-mango-600 transition-colors" data-testid="product-shop-link">{product.seller_name}</Link></p>
          <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight text-stone-900" data-testid="product-name">{product.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            <div className="flex items-center gap-1 text-sm text-stone-600"><Star className="w-4 h-4 fill-mango-600 text-mango-600" /> {product.rating?.toFixed(1)}</div>
            <span className="text-stone-300">·</span>
            <span className={`text-sm font-medium ${soldOut ? "text-red-600" : "text-emerald-700"}`}>{soldOut ? t("out_of_stock") : `${product.stock} ${t("in_stock")}`}</span>
          </div>
          <p className="font-display font-extrabold text-4xl text-emerald-800 my-5" data-testid="product-price">{fmtPrice(product.price)}</p>
          <p className="text-stone-600 leading-relaxed mb-6">{product.description}</p>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-stone-300 rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 grid place-items-center text-stone-600" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(product.stock, q + 1))} className="w-10 h-10 grid place-items-center text-stone-600" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button disabled={soldOut} onClick={() => { add(product, qty); toast.success(product.name, { description: t("add_to_cart") }); }}
              className="flex-1 rounded-full bg-emerald-800 hover:bg-emerald-900 h-12" data-testid="add-to-cart-btn">{t("add_to_cart")}</Button>
            <Button disabled={soldOut} onClick={buyNow} className="flex-1 rounded-full bg-mango-600 hover:bg-mango-700 h-12" data-testid="buy-now-btn">{t("buy_now")}</Button>
            <WishlistButton productId={product.id} className="w-12 h-12 border border-stone-300 hover:border-mango-600 shrink-0" size={20} />
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-stone-600"><Truck className="w-5 h-5 text-emerald-800" /> {t("trust_delivery_d")}</div>
            <div className="flex items-center gap-2 text-stone-600"><ShieldCheck className="w-5 h-5 text-emerald-800" /> {t("trust_secure_d")}</div>
          </div>
        </div>
      </div>

      {/* Detailed description sheet */}
      <div className="mt-16 max-w-4xl" data-testid="product-details-sheet">
        <Tabs defaultValue="description">
          <TabsList>
            <TabsTrigger value="description" data-testid="tab-description">{t("tab_description")}</TabsTrigger>
            <TabsTrigger value="specs" data-testid="tab-specs">{t("tab_specs")}</TabsTrigger>
            <TabsTrigger value="delivery" data-testid="tab-delivery">{t("tab_delivery")}</TabsTrigger>
          </TabsList>

          <TabsContent value="description" className="mt-5">
            <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
              <p className="text-stone-700 leading-relaxed whitespace-pre-wrap" data-testid="full-description">
                {product.description || t("no_description")}
              </p>
            </div>
          </TabsContent>

          <TabsContent value="specs" className="mt-5">
            <div className="bg-white border border-stone-200 rounded-xl p-2 shadow-sm">
              {[
                { icon: Tag, label: t("category"), value: CAT_LABELS[product.category]?.[lang] || product.category },
                ...(product.subcategory ? [{ icon: Tag, label: t("subcategory"), value: product.subcategory }] : []),
                { icon: Store, label: t("by"), value: product.seller_name },
                { icon: Package, label: t("in_stock"), value: product.stock > 0 ? `${product.stock}` : t("out_of_stock") },
                { icon: Star, label: t("reviews"), value: `${product.rating?.toFixed(1)} / 5 · ${product.reviews || 0}` },
                { icon: Tag, label: "Réf.", value: product.id.slice(0, 8).toUpperCase() },
                { icon: Clock, label: t("added_on"), value: new Date(product.created_at).toLocaleDateString() },
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-stone-100 last:border-0">
                  <span className="flex items-center gap-2 text-sm text-stone-500"><row.icon className="w-4 h-4 text-emerald-800" /> {row.label}</span>
                  <span className="text-sm font-medium text-stone-800 text-right">{row.value}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="delivery" className="mt-5">
            <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm space-y-6">
              <div>
                <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2"><Truck className="w-5 h-5 text-emerald-800" /> {t("delivery_options")}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="border border-stone-200 rounded-lg p-4" data-testid="delivery-standard">
                    <div className="flex items-center justify-between mb-1"><span className="font-medium flex items-center gap-2 text-stone-800"><Truck className="w-4 h-4 text-emerald-800" /> {t("standard")}</span><span className="font-display font-bold text-emerald-800">{fmtPrice(1000)}</span></div>
                    <p className="text-sm text-stone-500">{t("standard_eta")}</p>
                  </div>
                  <div className="border border-stone-200 rounded-lg p-4" data-testid="delivery-express">
                    <div className="flex items-center justify-between mb-1"><span className="font-medium flex items-center gap-2 text-stone-800"><Zap className="w-4 h-4 text-mango-600" /> {t("express")}</span><span className="font-display font-bold text-emerald-800">{fmtPrice(2500)}</span></div>
                    <p className="text-sm text-stone-500">{t("express_eta")}</p>
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3"><Clock className="w-5 h-5 text-emerald-800 mt-0.5" /><div><p className="font-medium text-stone-800">{t("opening_hours")}</p><p className="text-sm text-stone-500">{t("hours_weekdays")}</p><p className="text-sm text-stone-500">{t("hours_sunday")}</p></div></div>
                <div className="flex items-start gap-3"><Truck className="w-5 h-5 text-mango-600 mt-0.5" /><div><p className="font-medium text-stone-800">{t("delivery_hours")}</p><p className="text-sm text-stone-500">{t("delivery_window")}</p></div></div>
              </div>
              <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-emerald-800 mt-0.5" /><div><p className="font-medium text-stone-800">{t("delivery_zone")}</p><p className="text-sm text-stone-500">{t("delivery_info")}</p></div></div>
              <div className="border-t border-stone-100 pt-4 space-y-3">
                <div className="flex items-start gap-3"><Smartphone className="w-5 h-5 text-mango-600 mt-0.5" /><div><p className="font-medium text-stone-800">{t("mobile_money")}</p><p className="text-sm text-stone-500">Flooz · T-Money</p></div></div>
                <div className="flex items-start gap-3"><Banknote className="w-5 h-5 text-emerald-800 mt-0.5" /><div><p className="font-medium text-stone-800">{t("cash")}</p><p className="text-sm text-stone-500">{t("cash_info")}</p></div></div>
                <div className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-800 mt-0.5" /><div><p className="font-medium text-stone-800">{t("verified_seller")}</p><p className="text-sm text-stone-500">{t("trust_verified_d")}</p></div></div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {related.length > 0 && (
        <div className="mt-20">
          <h2 className="font-display font-extrabold text-2xl tracking-tight mb-6">{t("related")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
        </div>
      )}

      <Reviews productId={product.id} onRatingChange={refreshRating} />
    </div>
  );
}
